import { computed, ref, watch } from 'vue';
import { useSanityData } from '@pkg/sanity-utils/composables/useSanityData';
import { FILTER_ALL } from '~/app/types/listingTypes';
import { generateUniqueListingKey } from '~/app/helpers/listingKeyGenerator';

interface UseListingFiltersOptions {
  sanityKey?: string;
  listingType?: string;
  taxonomy?: string;
  postsPerPage?: number;
  initialActiveFilter?: Array<string> | null;
}

interface Taxonomy {
  _id: string;
  title: string;
  slug: string;
}

/**
 * Sync listing filter URL state (no fetches).
 * Call this before any `await` so listing `useFetch` can register in the same setup tick.
 */
export function useListingFilterState(options: UseListingFiltersOptions) {
  const route = useRoute();
  const router = useRouter();

  const listingKey = generateUniqueListingKey(
    options.sanityKey,
    options.listingType,
    options.taxonomy,
    options.postsPerPage,
  );

  const urlCategoryFilter = computed(() => {
    const categoryParam = route.query[`category_${listingKey}`];
    if (categoryParam && typeof categoryParam === 'string') {
      return categoryParam.split(',').filter(Boolean);
    }
    return null;
  });

  const effectiveActiveFilter = computed(() => {
    return urlCategoryFilter.value || options.initialActiveFilter;
  });

  const activeFilter = ref<Array<string>>(effectiveActiveFilter.value || [FILTER_ALL.slug]);

  watch(effectiveActiveFilter, (newFilter) => {
    activeFilter.value = newFilter || [FILTER_ALL.slug];
  }, { immediate: true });

  function updateFilter(filterSlug: Array<string>) {
    const currentQuery = { ...route.query };

    if (filterSlug[0] !== FILTER_ALL.slug) {
      currentQuery[`category_${listingKey}`] = filterSlug.join(',');
      delete currentQuery[`page_${listingKey}`];
    } else {
      delete currentQuery[`category_${listingKey}`];
      delete currentQuery[`page_${listingKey}`];
    }

    router.push({ query: currentQuery });
  }

  return {
    listingKey,
    activeFilter: readonly(activeFilter),
    updateFilter,
    effectiveActiveFilter: readonly(effectiveActiveFilter),
  };
}

/** Filter UI state + taxonomy options (for BaseFilter). */
export async function useListingFilters(options: UseListingFiltersOptions) {
  const { locale } = useI18n();
  const state = useListingFilterState(options);

  const query = groq`
    *[ _type == $taxonomy && language == "${unref(locale)}"] {
      _id,
      title,
      "slug": slug.current,
    }
  `;

  const { data: taxonomyList } = await useSanityData<Taxonomy[]>(query, {
    taxonomy: options.taxonomy,
  });

  return {
    ...state,
    taxonomyList,
  };
}
